import { detectEmbedProvider } from './embedProviders';
import { AUTO_RESOLUTION } from '../../shared/media/mediaResolution';

export const MEDIA_KINDS = {
  image: 'image',
  video: 'video',
  embed: 'embed',
};

export const DEFAULT_PLAYBACK = {
  controls: true,
  autoplay: false,
  loop: false,
  muted: false,
};

export const EMPTY_MEDIA_SOURCE = {
  kind: MEDIA_KINDS.image,
  src: '',
  poster: '',
  provider: '',
  /* Palier de résolution choisi dans la banque : `src` porte l'URL retenue,
     `srcset` la résolution intelligente quand le palier est `auto`, `mediaId`
     de quoi relire les paliers plus tard. Vides pour une URL collée à la main. */
  mediaId: null,
  resolution: AUTO_RESOLUTION,
  srcset: '',
  playback: { ...DEFAULT_PLAYBACK },
};

const VIDEO_EXT = /\.(mp4|webm|ogg|mov)(\?|#|$)/i;

export function inferMediaKindFromSrc(src) {
  if (!src || typeof src !== 'string') return MEDIA_KINDS.image;
  if (detectEmbedProvider(src)) return MEDIA_KINDS.embed;
  if (VIDEO_EXT.test(src)) return MEDIA_KINDS.video;
  return MEDIA_KINDS.image;
}

function defaultPlaybackForKind(kind) {
  if (kind === MEDIA_KINDS.video || kind === MEDIA_KINDS.embed) {
    return { controls: true, autoplay: false, loop: false, muted: false };
  }
  return { ...DEFAULT_PLAYBACK };
}

/** Normalise une URL legacy (string) ou un objet MediaSource. */
export function normalizeMediaSource(value) {
  if (value == null) return { ...EMPTY_MEDIA_SOURCE, playback: { ...DEFAULT_PLAYBACK } };
  if (typeof value === 'string') {
    const src = value.trim();
    if (!src) return { ...EMPTY_MEDIA_SOURCE, playback: { ...DEFAULT_PLAYBACK } };
    const kind = inferMediaKindFromSrc(src);
    const provider = kind === MEDIA_KINDS.embed ? (detectEmbedProvider(src) || '') : '';
    return {
      ...EMPTY_MEDIA_SOURCE,
      kind,
      src,
      provider,
      playback: defaultPlaybackForKind(kind),
    };
  }
  if (typeof value === 'object') {
    const src = typeof value.src === 'string' ? value.src.trim() : '';
    const kind = value.kind || inferMediaKindFromSrc(src);
    const provider = value.provider
      || (kind === MEDIA_KINDS.embed ? (detectEmbedProvider(src) || '') : '');
    return {
      ...EMPTY_MEDIA_SOURCE,
      ...value,
      kind,
      src,
      provider,
      resolution: value.resolution || AUTO_RESOLUTION,
      srcset: value.srcset || '',
      mediaId: value.mediaId ?? null,
      playback: {
        ...defaultPlaybackForKind(kind),
        ...(value.playback || {}),
      },
    };
  }
  return { ...EMPTY_MEDIA_SOURCE, playback: { ...DEFAULT_PLAYBACK } };
}

export function getMediaSrc(value) {
  return normalizeMediaSource(value).src;
}

export function isMediaPresent(value) {
  return Boolean(getMediaSrc(value));
}

/** Lecture en fond (hero, carousel) : muet, boucle, sans contrôles par défaut. */
export function resolveBackgroundPlayback(media) {
  const m = normalizeMediaSource(media);
  if (m.kind !== MEDIA_KINDS.video) return null;
  return {
    ...m.playback,
    controls: false,
    autoplay: m.playback?.autoplay !== false,
    loop: m.playback?.loop !== false,
    muted: true,
  };
}

export function resolveInlinePlayback(media, { isEditing = false } = {}) {
  const m = normalizeMediaSource(media);
  if (m.kind !== MEDIA_KINDS.video && m.kind !== MEDIA_KINDS.embed) return m.playback;
  if (isEditing) {
    return {
      ...m.playback,
      autoplay: false,
    };
  }
  return m.playback;
}
